import * as actionTypes from '@/services/store/action-types'
import { Store } from 'vuex'

export const beforeEach = async (
  to: any,
  from: any,
  next: any,
  errorHandler: any,
  store: Store<any>,
) => {
  // Clear errors
  store.set('app/error', null)

  // Save accountID into store
  const accountID = to.params.accountId
  store.set('app/accountID', accountID)
  const notificationIntervalID = store.get('app/notificationIntervalID')

  // Clear notifications if no account ID
  if (!accountID && notificationIntervalID) {
    store.set('app/notificationIntervalID', null)
    clearInterval(notificationIntervalID)
  }

  // If we're in an account...
  if (accountID && !isAccountMembershipAgnostic(to)) {
    // Set user profile in store if not there (e.g. first refresh)...
    if (!store.state.profile.data.id) {
      store.set('app/isLoading', true)
      try {
        // Store PROFILE DATA
        await store.dispatch(`profile/${actionTypes.PROFILE_FETCH}`)
      } catch (error) {
        errorHandler.emit(error, {})
        if (error.status !== 402) {
          store.set('app/isLoading', false)
        }
      }
    }

    // If non-AO is trying to access an AO route, send to forbidden screen...
    if (isAdminOwnerOnly(to) && !store.get('profile/isAdminOrOwner')) {
      store.set('app/isLoading', false)
      store.set('app/error', 403)
      return
    }

    if (!store.state.profile.data.profileMetrics) {
      try {
        // Store METRICS data and push info to GTM
        await store.dispatch(`profile/${actionTypes.PROFILE_METRICS_FETCH}`)

        const profile = store.state.profile.data
        const profileMetrics = store.state.profile.data.profileMetrics

        if (
          profileMetrics &&
          profileMetrics.userData &&
          profileMetrics.userData.accountMembershipIdentifier
        ) {
          // Appcues identification
          // @ts-ignore
          window.Appcues.identify(
            profileMetrics.userData.accountMembershipIdentifier,
            {
              ...profileMetrics.userData,
            },
          )

          // GTM: Add data layer vars
          // @ts-ignore
          window.dataLayer.push({
            userId: profileMetrics.userData.accountMembershipIdentifier,
            email: profileMetrics.userData.email,
            createDateUser: profileMetrics.userData.createDateUser,
            createdDateAccount: profileMetrics.userData.createdDateAccount,
            accountUserAddedDate: profileMetrics.userData.accountUserAddedDate,
            companyName: profileMetrics.userData.company,
            companyIndustry: profileMetrics.userData.companyIndustry,
            companySize: profileMetrics.userData.companySize,
            accountName: profileMetrics.userData.accountName,
            accountId: profileMetrics.userData.accountID,
            accountTeamTotal: profileMetrics.userData.accountTeamTotal,
            accountCompanyTotal: profileMetrics.userData.accountCompanyTotal,
            accountProjectTotal: profileMetrics.userData.accountProjectTotal,
            accountMailboxTotal: profileMetrics.userData.accountMailboxTotal,
            accountTaskTotal: profileMetrics.userData.accountTaskTotal,
            accountConversationTotal:
              profileMetrics.userData.accountConversationTotal,
            userProjectTotal: profileMetrics.userData.userProjectTotal,
            userMailboxTotal: profileMetrics.userData.userMailboxTotal,
            isAccountCreator: profileMetrics.userData.isAccountCreator,
            isAppSumoAccount: profileMetrics.userData.isAppSumoAccount,
            billingTerm: profileMetrics.userData.billingTerm,
            planName: profileMetrics.userData.planName,
            planTier: profileMetrics.userData.planTier,
            planType: profileMetrics.userData.planType,
            accountRole: profileMetrics.userData.role,
            firstName: profileMetrics.userData.firstName,
            lastName: profileMetrics.userData.lastName,
            displayName:
              profileMetrics.userData.firstName +
              ' ' +
              profileMetrics.userData.lastName,
            webexEmbeddedAppEnabled: profile.fromWebex ? 1 : 0,
          })
          // @ts-ignore
          window.dataLayer.push({ event: 'dataLayerVarsLoaded' })
          // @ts-ignore
          window.dataLayer.push({ event: 'dataLayerAndAppcuesLoaded' })
        } else {
          // @ts-ignore
          window.Appcues.anonymous()
        }
        store.set('app/isLoading', false)
      } catch (error) {
        errorHandler.emit(error, {})
        if (error.status !== 402) {
          store.set('app/isLoading', false)
        }
      }
    }

    // Send Webex share URL to GTM, if applicable...
    // Gotcha: Because the webex initialization is async (and GTM
    // doesn't support ECMAScript), I do a setInterval() to load
    // WebEx. This usually means the very first time we push the 'newRouteForWebex'
    // event which uses the webex js obj to pass the newUrl, it fails since
    // WebEx's JS object hasn't initalized and there is no wait period between the
    // datalayer push above and this call below. Hence, you may see the "Open Together" button
    // only show up starting on the second route change (when WebEx has presumably finished loading).
    if (store.state.profile.data.fromWebex) {
      const newUrl = process.env.VUE_APP_URL + to.fullPath
      // @ts-ignore
      window.dataLayer.push({
        event: 'newRouteForWebex',
        currentWebAppRoute: newUrl,
      })
    }

    // Start notification polling, if needed...
    if (store.get('app/notificationIntervalID') === null) {
      await store.dispatch(`profile/${actionTypes.NOTIFICATIONS_FETCH}`)
      const newIntervalID = setInterval(async () => {
        await store.dispatch(`profile/${actionTypes.NOTIFICATIONS_FETCH}`)
      }, 60000)
      store.set('app/notificationIntervalID', newIntervalID)
    }
  }
  next()
}

const isAdminOwnerOnly = (to: any) =>
  to.matched.some((record: any) => record.meta.adminOwnerOnly)

const isAccountMembershipAgnostic = (to: any) =>
  to.matched.some((record: any) => record.meta.accountMembershipAgnostic)
